import { useState } from 'react';
import { Send, Trash2 } from 'lucide-react';
import { useDescartarRascunho, usePublicar, useRascunho } from '@/api/liberacao';
import { usePode } from '@/api/me';
import { useCondicoesPagamento } from '@/api/condicoes-pagamento';
import { AcoesLote } from '@/components/liberacao/acoes-lote';
import { PublicarDialog } from '@/components/lista-precos/rascunho-publicacao';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api';
import { formatarContagem, formatarMoeda } from '@/lib/format';

function mensagemErro(e: unknown): string {
  return e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API';
}

/**
 * "Preços em lote — todos os itens" (S28-P2 na Formação; era "Liberação em lote — todas as tabelas", renomeado na S36): acoes em lote (sempre no rascunho) para todos os itens,
 * a lista do rascunho, "Descartar rascunho" e "Publicar…". So para quem tem praticado.gravar (a Formacao decide a politica
 * TABELA). Nenhum preco e calculado aqui: totais e linhas vem de GET /precificacao/rascunho.
 */
export function LiberacaoLote() {
  const { pode } = usePode();
  const rascunho = useRascunho();
  const condicoes = useCondicoesPagamento({ ativa: true, limit: 200 });
  const publicar = usePublicar();
  const descartar = useDescartarRascunho();
  const [publicarAberto, setPublicarAberto] = useState(false);
  const [descartarAberto, setDescartarAberto] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  if (!pode('praticado.gravar')) return null;

  const total = rascunho.data?.totalPrecos ?? 0;
  const itens = rascunho.data?.totalItens ?? 0;
  const linhas = rascunho.data?.linhas ?? [];
  const nomeCondicao = (id: string) => condicoes.data?.find((c) => c.id === id)?.nome ?? 'condição';

  return (
    <section aria-label="Preços em lote — todos os itens" data-testid="liberacao-lote" className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-title font-semibold">Preços em lote — todos os itens</h2>
          <p className="mt-1 text-small text-ink-secondary">
            Aplica uma ação a todos os itens (todas as tabelas), mostra o rascunho e publica a lista de uma vez. Só vale depois de publicar.
          </p>
        </div>
        <AcoesLote escopo={{}} rotuloEscopo="todos os itens, todas as tabelas" />
      </div>

      {erro && (
        <p role="alert" className="rounded-xl border border-destructive/30 bg-danger-bg px-4 py-3 text-small text-danger-ink">
          {erro}
        </p>
      )}
      {aviso && (
        <p role="status" className="text-small text-ink-secondary">
          {aviso}
        </p>
      )}

      {total > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-small text-ink">
          <span>
            Rascunho: {formatarContagem(total)} {total === 1 ? 'preço' : 'preços'} em {formatarContagem(itens)} {itens === 1 ? 'item' : 'itens'}, não publicados
          </span>
          <span className="flex flex-wrap items-center gap-2">
            <Button type="button" size="sm" onClick={() => { setAviso(null); publicar.reset(); setPublicarAberto(true); }}>
              <Send aria-hidden="true" className="size-4" />
              Publicar…
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => { setErro(null); setDescartarAberto(true); }}>
              <Trash2 aria-hidden="true" className="size-4" />
              Descartar rascunho
            </Button>
          </span>
        </div>
      ) : (
        <p className="text-small text-ink-muted">Nada no rascunho.</p>
      )}

      {linhas.length > 0 && (
        <ul aria-label="Preços do rascunho" className="flex max-h-[320px] flex-col divide-y divide-border overflow-y-auto text-small">
          {linhas.map((l) => (
            <li key={`${l.itemId}|${l.tabelaId}|${l.condicaoId}`} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <span className="min-w-0 break-words">
                <span className="font-medium">{l.itemCodigo}</span> · {l.itemDescricao}
                <span className="text-ink-muted"> · {l.tabelaCodigo} · {nomeCondicao(l.condicaoId)}</span>
              </span>
              <span className="tabular flex items-center gap-1.5 font-semibold">
                {formatarMoeda(l.preco)}
                {l.manual && <span className="rounded-full bg-warning-bg px-2 text-caption font-medium leading-5 text-warning-ink">manual</span>}
              </span>
            </li>
          ))}
        </ul>
      )}

      <PublicarDialog
        aberto={publicarAberto}
        onAberto={setPublicarAberto}
        total={total}
        pendente={publicar.isPending}
        onPublicar={async (dto) => {
          const r = await publicar.mutateAsync(dto);
          setAviso(
            `Lista publicada: ${formatarContagem(r.totalPrecos)} ${r.totalPrecos === 1 ? 'preço' : 'preços'} (${formatarContagem(r.marcasResolvidas)} ${r.marcasResolvidas === 1 ? 'marca de revisão resolvida' : 'marcas de revisão resolvidas'})`,
          );
        }}
      />

      <AlertDialog open={descartarAberto} onOpenChange={setDescartarAberto}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar o rascunho?</AlertDialogTitle>
            <AlertDialogDescription>Os {formatarContagem(total)} preços não publicados saem do rascunho. A lista publicada não muda.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Manter rascunho</AlertDialogCancel>
            <AlertDialogAction
              onClick={(ev) => {
                ev.preventDefault();
                descartar.mutate(undefined, {
                  onSuccess: () => {
                    setDescartarAberto(false);
                    setAviso('Rascunho descartado');
                  },
                  onError: (e) => {
                    setDescartarAberto(false);
                    setErro(`Não foi possível descartar o rascunho: ${mensagemErro(e)}`);
                  },
                });
              }}
            >
              Descartar rascunho
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
